<template>
	<form @submit.prevent="handleSubmit">
		<div class="omapi-integrations-form__uncanny-automator">
			<div class="omapi-integrations-form__uncanny-automator__container">
				<div
					class="omapi-integrations-form__uncanny-automator__container-item omapi-integrations-form__uncanny-automator__intro"
				>
					Uncanny Automator integrates OptinMonster with 150+ WordPress plugins and apps to make your
					campaigns more powerful in two ways:
				</div>

				<div
					class="omapi-integrations-form__uncanny-automator__container-item omapi-integrations-form__uncanny-automator__mascot"
				>
					<img src="../../../assets/images/uncanny-automator-mascot.png" alt="Uncanny Automator Mascot" />
				</div>

				<ol
					class="omapi-integrations-form__uncanny-automator__container-item omapi-integrations-form__uncanny-automator__details"
				>
					<li>
						<p>
							<strong>Advanced campaign display rules:</strong> Use Automator triggers to display
							campaigns when users:
						</p>
						<ul>
							<li>Fill out a form</li>
							<li>Join a group</li>
							<li>Purchase or renew a product or membership</li>
							<li>Complete a course</li>
							<li>Update their profile</li>
							<li>Complete any sequence of steps you define with hundreds of available triggers</li>
						</ul>
					</li>
					<li>
						<p>
							<strong>Campaign-initiated automations:</strong> Use Automator actions to automatically do
							something when users submit a campaign, like:
						</p>
						<ul>
							<li>Generate and email a coupon code</li>
							<li>Register the user on your site</li>
							<li>Enroll the user in a course or group</li>
							<li>Send an update to Slack, Google Sheets, Facebook, Instagram, Twitter/X and more</li>
							<li>Complete any workflow you define with hundreds of available actions</li>
						</ul>
					</li>
				</ol>

				<div
					class="omapi-integrations-form__uncanny-automator__container-item omapi-integrations-form__uncanny-automator__integration-icon"
				>
					<img
						src="../../../assets/images/uncanny-automator-for-optinmonster.png"
						alt="Uncanny Automator Integration"
					/>
				</div>

				<p
					class="omapi-integrations-form__uncanny-automator__container-item omapi-integrations-form__uncanny-automator__integration-last-words"
				>
					Get started in 5 minutes with an easy-to-use, no-code automation builder.
				</p>
			</div>
		</div>

		<div :class="ctaClass">
			<core-loading-button color="green" button-type="submit" :disabled="submitting" :loading="submitting">
				{{ buttonText }}
			</core-loading-button>
			<core-button :disabled="submitting" @click="resetProviders"> Cancel </core-button>
		</div>
	</form>
</template>
<script>
import { form } from '@/integrations/mixins/form';
import { integration } from '@/integrations/mixins/integration';
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex';

export default {
	mixins: [form, integration],
	props: {
		provider: {
			type: Object,
			required: true,
		},
	},
	data() {
		return {
			submitting: false,
			plugin: {},
		};
	},
	computed: {
		...mapState('plugins', ['plugins']),
		...mapGetters('plugins', ['bySlug']),
		isPluginActive() {
			return this.plugin?.active;
		},
		buttonText() {
			if (!this.hasIntegrations) {
				return `Install ${this.provider.name}`;
			}
			return this.isPluginActive ? 'Go to Dashboard' : `Activate ${this.provider.name}`;
		},
		ctaClass() {
			return this.hasIntegrations ? 'omapi-integrations-form__cta' : 'omapi-integration-cta';
		},
	},
	mounted() {
		this.fetchPlugins();
		this.plugin = this.bySlug(this.provider.slug.replace('-featured', '').replace('-existing', ''));
	},
	methods: {
		...mapMutations({
			setActiveContentMessage: 'integrations/setActiveContentMessage',
			setActiveContentError: 'integrations/setActiveContentError',
		}),
		...mapActions({
			addIntegration: 'integrations/addIntegration',
			resetProviders: 'integrations/resetProviders',
			resetIntegrations: 'integrations/resetIntegrations',
			fetchPlugins: 'plugins/fetch',
			installPlugin: 'plugins/action',
		}),
		/**
		 * Handle Button Action
		 *
		 * @since 2.16.0
		 *
		 * @returns {void}
		 */
		handleSubmit() {
			if (this.submitting) {
				return;
			}

			this.submitting = true;

			if (this.isPluginActive) {
				window.location.href = this.plugin?.data?.dashboard_url;
				return;
			}

			this.installPlugin(this.plugin)
				.then(() => {
					const action = !this.isPluginActive ? 'activated' : 'installed';
					this.$store.commit(
						'maybeAddAlert',
						{
							message: `Plugin ${action}! Refreshing...`,
							type: 'info',
							closes: true,
						},
						{ root: true }
					);

					setTimeout(() => {
						window.location.reload();
					}, 500);
				})
				.catch((error) => {
					// eslint-disable-next-line no-console
					console.error({ error });

					const action = !this.isPluginActive ? 'activate' : 'install';
					this.setActiveContentError(`Unable to ${action} ${this.provider.name}: ${error}`);
				})
				.finally(() => {
					this.submitting = false;
				});
		},
	},
};
</script>

<style lang="scss" scoped>
@import '../../../assets/scss/variables';

.omapi-integrations-form__uncanny-automator {
	&__container {
		display: grid;
		grid-template-columns: repeat(12, 1fr);
		grid-template-rows: repeat(12, 40px);
		gap: 10px;

		* {
			font-size: 15px !important;
			font-weight: 400 !important;
			line-height: 1.6 !important;
			color: $font_setting-color !important;

			@media (max-width: 959px) {
				font-size: 12px !important;
				line-height: 1.2 !important;
			}
		}

		strong {
			font-weight: 600 !important;
		}

		ul {
			list-style: inside !important;
		}

		ol {
			> li:not(:last-child) {
				margin-bottom: 25px;

				@media (max-width: 959px) {
					margin-bottom: 20px !important;
				}
			}
		}

		@media (max-width: 959px) {
			grid-template-columns: 1fr;

			&-item {
				grid-column: span 12 !important;
				align-self: auto;
			}

			ul {
				li {
					margin-bottom: 2px !important;
				}
			}

			p {
				margin: 0 0 10px !important;
			}
		}
	}

	&__intro {
		grid-column: span 9;
		grid-row: span 1;
		font-weight: 600 !important;

		@media (max-width: 959px) {
			grid-row: span 2;
		}
	}

	&__mascot {
		grid-column: span 3;
		grid-row: span 3;
		justify-self: end;

		img {
			height: 100%;
			width: auto;
		}

		@media (max-width: 959px) {
			display: none;
		}
	}

	&__details {
		grid-column: span 7;
		grid-row: span 9;

		@media (max-width: 959px) {
			grid-row: span 8;
		}
	}

	&__integration-icon {
		grid-column: span 5;
		grid-row: span 9;
		align-self: end;

		img {
			width: 100%;
			height: auto;
			border-radius: 8px;
		}

		@media (max-width: 959px) {
			display: none;
		}
	}

	&__integration-last-words {
		grid-column: span 7;
		grid-row: 12;
	}
}
</style>
